/**
 * Make the inline ZipChat embed the official chat iframe so PDP questions
 * post into it instead of opening the corner bubble.
 *
 * ZipChat's /iframe/ embed reads document.referrer (usually origin only).
 * Rewrite it to /widget with current_url so the chat form gets the product,
 * the same way the floating bubble does.
 * Each product gets its own visitor_token so chats do not carry across
 * PDPs. Returning to the same product reloads that product's history.
 */
(function () {
  const ZIPCHAT_ORIGIN = 'https://app.zipchat.ai';
  const TOKEN_TTL_MS = 7 * 24 * 60 * 60 * 1000;
  const TOKEN_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

  function getEmbedRoot() {
    return document.querySelector('.zipchat-embed');
  }

  function getEmbedIframe() {
    return document.querySelector('.zipchat-embed iframe');
  }

  function getProductHandle() {
    const root = getEmbedRoot();
    return (root && root.getAttribute('data-zipchat-product-handle')) || '';
  }

  function getPageUrl() {
    const root = getEmbedRoot();
    const canonical = root && root.getAttribute('data-zipchat-current-url');
    if (canonical) return canonical;
    try {
      const url = new URL(window.location.href);
      url.hash = '';
      return url.toString();
    } catch (err) {
      return window.location.href.split('#')[0];
    }
  }

  function randomToken() {
    let token = '';
    for (let i = 0; i < 20; i++) {
      token += TOKEN_CHARS.charAt(Math.floor(Math.random() * TOKEN_CHARS.length));
    }
    return token;
  }

  function readStoredToken(key) {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      const item = JSON.parse(raw);
      if (item.expiration && Date.now() > item.expiration) {
        localStorage.removeItem(key);
        return null;
      }
      return item.value != null ? item.value : raw;
    } catch (err) {
      return null;
    }
  }

  function storeToken(key, token) {
    try {
      localStorage.setItem(
        key,
        JSON.stringify({ value: token, expiration: Date.now() + TOKEN_TTL_MS })
      );
    } catch (err) {}
  }

  function getVisitorToken() {
    const handle = getProductHandle();
    if (handle) {
      const key = 'zipchat_pdp_visitor_' + handle;
      let token = readStoredToken(key);
      if (!token) {
        token = randomToken();
        storeToken(key, token);
      }
      return token;
    }
    if (window.Zipchat && typeof Zipchat.getVisitorToken === 'function') {
      return Zipchat.getVisitorToken();
    }
    return readStoredToken('visitor_token');
  }

  function applyProductContext(iframe) {
    if (!iframe || !iframe.getAttribute('src')) return false;
    let url;
    try {
      url = new URL(iframe.src, window.location.href);
    } catch (err) {
      return false;
    }
    if (url.origin !== ZIPCHAT_ORIGIN) return false;

    const pageUrl = getPageUrl();
    const token = getVisitorToken();
    let changed = false;

    // /iframe/ ignores current_url and uses document.referrer (origin only).
    // /widget bakes the product URL into the chat form, same as the bubble.
    if (url.pathname === '/iframe/' || url.pathname === '/iframe') {
      url.pathname = '/widget';
      changed = true;
    }
    if (url.searchParams.get('current_url') !== pageUrl) {
      url.searchParams.set('current_url', pageUrl);
      changed = true;
    }
    if (token && url.searchParams.get('visitor_token') !== String(token)) {
      url.searchParams.set('visitor_token', String(token));
      changed = true;
    }
    if (!url.searchParams.has('visitor_width')) {
      url.searchParams.set('visitor_width', String(window.innerWidth));
      changed = true;
    }

    if (iframe.getAttribute('referrerpolicy') !== 'unsafe-url') {
      iframe.setAttribute('referrerpolicy', 'unsafe-url');
    }

    if (changed) setIframeSrc(iframe, url.toString());
    return changed;
  }

  function setIframeSrc(iframe, nextSrc) {
    if (!iframe || iframe.getAttribute('src') === nextSrc) return;
    const x = window.scrollX;
    const y = window.scrollY;
    const restore = function () {
      window.scrollTo(x, y);
    };
    iframe.addEventListener('load', restore, { once: true });
    iframe.src = nextSrc;
    restore();
    requestAnimationFrame(restore);
  }

  function hideBubbleButton() {
    const host =
      (window.Zipchat && Zipchat._shadowHost) || document.getElementById('zipchat-shadow-host');
    if (host) {
      host.style.setProperty('display', 'none', 'important');
      host.style.setProperty('visibility', 'hidden', 'important');
      host.style.setProperty('pointer-events', 'none', 'important');
      host.setAttribute('aria-hidden', 'true');
    }

    const root = (window.Zipchat && Zipchat._shadowRoot) || (host && host.shadowRoot);
    if (!root) return;

    if (!root.getElementById('zipchat-pdp-hide-bubble')) {
      const style = document.createElement('style');
      style.id = 'zipchat-pdp-hide-bubble';
      style.textContent =
        '#widget-chat-container,#widget-chat-button,#chat-preview-message{display:none!important;visibility:hidden!important;pointer-events:none!important;}';
      root.appendChild(style);
    }

    ['widget-chat-container', 'widget-chat-button', 'chat-preview-message'].forEach(function (id) {
      const el = root.getElementById(id);
      if (!el) return;
      el.style.setProperty('display', 'none', 'important');
      el.style.setProperty('visibility', 'hidden', 'important');
      el.style.setProperty('pointer-events', 'none', 'important');
    });
  }

  function useEmbedAsWidgetIframe(options) {
    const embed = getEmbedIframe();
    if (!embed) return null;
    embed.id = 'zipchat-iframe';
    embed.classList.add('zipchat-iframe');
    embed.setAttribute('data-zipchat-visible', 'true');
    embed.style.opacity = '1';
    embed.style.pointerEvents = 'all';
    embed.style.transform = 'none';
    if (options && options.scroll) {
      embed.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    try {
      embed.contentWindow.postMessage(
        { action: 'iframeVisible', origin: window.location.origin },
        ZIPCHAT_ORIGIN
      );
    } catch (err) {}
    hideBubbleButton();
    return embed;
  }

  let patched = false;

  function patchZipchat() {
    if (patched || !window.Zipchat) return;
    if (typeof Zipchat.createChatbotIframe !== 'function') return;
    patched = true;

    const originalCreate = Zipchat.createChatbotIframe.bind(Zipchat);
    const originalHandle = Zipchat.handleIframe ? Zipchat.handleIframe.bind(Zipchat) : null;
    const originalClose = Zipchat.closeIframe ? Zipchat.closeIframe.bind(Zipchat) : null;

    Zipchat.createChatbotIframe = function () {
      if (useEmbedAsWidgetIframe()) return;
      return originalCreate.apply(this, arguments);
    };

    if (originalHandle) {
      Zipchat.handleIframe = function (iframe) {
        if (iframe && iframe.closest('.zipchat-embed')) {
          useEmbedAsWidgetIframe();
          return;
        }
        return originalHandle.apply(this, arguments);
      };
    }

    if (originalClose) {
      Zipchat.closeIframe = function (iframe) {
        if (iframe && iframe.closest('.zipchat-embed')) return;
        return originalClose.apply(this, arguments);
      };
    }

    applyProductContext(getEmbedIframe());
    useEmbedAsWidgetIframe();
    hideBubbleButton();
  }

  applyProductContext(getEmbedIframe());
  hideBubbleButton();

  document.addEventListener(
    'click',
    function (event) {
      const path = event.composedPath ? event.composedPath() : [];
      const fromQuestions = path.some(function (node) {
        return node && node.hasAttribute && node.hasAttribute('data-zipchat-pdp-questions');
      });
      if (!fromQuestions) return;
      const embed = getEmbedIframe();
      if (embed) embed.scrollIntoView({ behavior: 'smooth', block: 'center' });
    },
    true
  );

  const poll = window.setInterval(function () {
    hideBubbleButton();
    patchZipchat();
    if (patched) window.clearInterval(poll);
  }, 50);

  const observer = new MutationObserver(function () {
    hideBubbleButton();
    patchZipchat();
    const bubble = document.getElementById('zipchat-iframe');
    if (bubble && !bubble.closest('.zipchat-embed')) {
      const embed = getEmbedIframe();
      if (embed && embed !== bubble) {
        bubble.remove();
        useEmbedAsWidgetIframe();
      }
    }
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', patchZipchat);
  } else {
    patchZipchat();
  }
})();
